import { useEffect, useState } from "react";
import { harvestsApi } from "../../services/harvestsApi";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { Button } from "../../components/common/Button";
import { Modal } from "../../components/common/Modal";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { Field } from "../../components/common/Field";
import { HivePicker } from "../../components/common/HivePicker";
import { SitePicker } from "../../components/common/SitePicker";
import { VarietalPicker } from "../../components/common/VarietalPicker";
import { hivesApi } from "../../services/hivesApi";
import { WithdrawalNotice } from "../../components/hives/WithdrawalNotice";
import { hiveLabel } from "../../services/hiveActions";
import { toPounds } from "../../services/honeyUnits";
import { DataTable } from "../../components/common/DataTable";
import { Tabs } from "../../components/common/Tabs";
import { Pager } from "../../components/common/Pager";
import { LockedFeature } from "../../components/common/LockedFeature";
import { useToast } from "../../context/ToastContext";
import { useFlow } from "../../hooks/useFlow";
import { FLOWS } from "../../services/flows";

const TYPES = ["Honey", "Wax", "Propolis", "Pollen"];
const UNITS = ["Lbs", "Kg", "Gal", "L"];
const today = () => new Date().toISOString().slice(0, 10);

/**
 * This page could log nothing and remove nothing (#250).
 *
 * It was a viewer over a table -- four tabs, a row count, and no way to put
 * anything in or take anything out. POST /harvests had existed the whole
 * time; DELETE was added alongside this.
 */
export function HarvestsPage() {
  const { hasFeature } = useFeatureTheme();
  const { notify } = useToast();
  const [harvests, setHarvests] = useState(null);
  // Which tab is showing. It drives the REQUEST now, not a filter over
  // rows already fetched -- see the load() note.
  const [activeType, setActiveType] = useState(TYPES[0]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(null);
  const [limit, setLimit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // #379, keyed to the dialog rather than the page -- reading the
  // harvest list is not an abandoned harvest.
  const flow = useFlow(FLOWS.HARVEST, addOpen);
  const [busy, setBusy] = useState(false);
  const [withdrawal, setWithdrawal] = useState([]);
  // The row awaiting confirmation, or null (#328).
  const [pendingDelete, setPendingDelete] = useState(null);
  // Loaded once and filtered locally by site, rather than re-fetching on
  // every site change: the list is small, and a request per dropdown
  // change makes the hive field flicker empty between the two.
  const [hives, setHives] = useState([]);
  const [form, setForm] = useState({
    site_id: "", hive_id: "", harvest_type: "Honey", quantity: "", unit: "Lbs",
    date: today(), notes: "", varietal_id: "", credit_pool: true,
  });

  /*
   * Paged (#329). The endpoint has always returned 50 rows and the page
   * has always shown them with nothing to say more existed -- 50 of 312
   * looked exactly like all 312.
   *
   * offset lives in state and load() reads it, so paging is one request
   * rather than a client-side slice of a set the server never sent.
   */
  function load(nextOffset = offset, type = activeType) {
    setLoading(true);
    harvestsApi.list({ offset: nextOffset, harvest_type: type })
      .then((r) => {
        setHarvests(r.harvests);
        setTotal(r.total ?? null);
        setLimit(r.limit ?? null);
      })
      .catch(() => setHarvests([]))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  function goTo(nextOffset) {
    setOffset(nextOffset);
    load(nextOffset);
  }

  // Back to page one on a tab change. Carrying offset 100 into a tab
  // with 12 rows shows an empty list and a pager saying 101-112 of 12.
  function changeTab(type) {
    setActiveType(type);
    setOffset(0);
    load(0, type);
  }
  useEffect(() => {
    hivesApi.list({ status: "Active" }).then((r) => setHives(r.hives || [])).catch(() => setHives([]));
    /* Honey that must not go in a jar yet (#404). Harvest is the direct
       case -- honey leaving the hive -- and this is the form where it
       leaves. Best-effort: a failure says nothing rather than blocking a
       harvest somebody needs to record. */
    hivesApi.withdrawal().then((r) => setWithdrawal(r.withdrawal || [])).catch(() => {});
  }, []);

  async function submit() {
    /* `> 0`, not falsy: -5 is truthy, and a negative harvest would credit
       the pool the wrong way before the schema's gt=0 caught it.

       SPLIT, because one message for two questions said neither.
       Reported from a device: the negative was refused "but the error
       message always states 'Which hive, and how much?'" -- which names
       the hive first, on a form where the hive was already chosen. A
       refusal that asks about the part you got right reads as the app
       not having looked. */
    if (!form.hive_id) {
      notify("Which hive did this come from?", "warning");
      return;
    }
    if (!(Number(form.quantity) > 0)) {
      notify("How much did you take? Enter a quantity greater than zero.", "warning");
      return;
    }
    setBusy(true);
    try {
      const isHoneyNow = form.harvest_type === "Honey";
      const r = await harvestsApi.create({
        hive_id: Number(form.hive_id),
        harvest_type: form.harvest_type,
        quantity: Number(form.quantity),
        unit: form.unit,
        date: form.date,
        notes: form.notes.trim() || undefined,
        // Sent only for honey. varietal_id "" means the account's General
        // varietal, which the server resolves -- the column is written at
        // every tier even though the picker is Business-only.
        varietal_id: isHoneyNow && form.varietal_id ? Number(form.varietal_id) : undefined,
        credit_pool: isHoneyNow ? form.credit_pool : false,
      });
      flow.finished();
      // Say what happened to the pool, using the SERVER's answer rather
      // than the local estimate -- the two agree today, and if they ever
      // stop, the number the user is told is the one that was banked.
      notify(
        r.pool_movement_id
          ? "Harvest logged and added to the honey pool"
          : "Harvest logged",
        "success",
      );
      /*
       * A second toast, warning, when the server thinks this looks like
       * one already logged.
       *
       * Reported from dev: two 25 lb harvests on one hive raised nothing
       * at the time. Detection is a read-time pass by design -- offline
       * is exactly when the other entry has not arrived -- but when both
       * come in online, the moment of the second one is when the person
       * is still standing there and can say "no, that was two supers".
       *
       * NOT a blocking dialog. It might genuinely be two supers, and a
       * modal that has to be dismissed to record real work is how people
       * learn to click through warnings. The pool consequence is named,
       * because that is what makes this worth interrupting for at all.
       */
      if (r.duplicate_suspected) {
        notify(
          "That looks like a harvest already logged for this hive today. "
          + "Check the duplicate question on your dashboard — if it is the same "
          + "one twice, the pool has been credited twice.",
          "warning",
        );
      }
      setAddOpen(false);
      // Site, unit and date survive: logging several hives from one yard
      // in a session is the normal case, and re-picking the yard every
      // time is the kind of friction that gets a page abandoned.
      setForm((f) => ({ ...f, hive_id: "", quantity: "", notes: "" }));
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not log that harvest", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function remove(row) {
    // ConfirmDialog, not window.confirm (#328). A browser dialog can be
    // switched off by the user -- "prevent this page from creating
    // additional dialogs" makes it return false SILENTLY, so the guard
    // stops appearing on a delete that has no undo. It is also unthemed
    // and reads like a browser error on mobile web.
    //
    // The numbers stay in the sentence so somebody skim-reading still
    // sees WHICH row is about to go.
    setPendingDelete(row);
  }

  async function confirmDelete() {
    const row = pendingDelete;
    if (!row) return;
    setBusy(true);
    try {
      await harvestsApi.remove(row.harvest_id);
      notify("Harvest removed", "success");
      setPendingDelete(null);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not remove that harvest", "danger");
    } finally {
      setBusy(false);
    }
  }

  const isHoney = form.harvest_type === "Honey";
  // "" means no site chosen -- show everything, rather than nothing.
  const sitedHives = form.site_id
    ? hives.filter((h) => String(h.site_id) === String(form.site_id))
    : hives;
  // What the pool will actually gain. Shown, never sent: the server does
  // the conversion that reaches the ledger.
  const poolPounds = isHoney && form.credit_pool ? toPounds(form.quantity, form.unit) : null;

  const columns = [
    { key: "date", header: "Date", render: (r) => new Date(r.date).toLocaleDateString() },
    // The name, not the key. This column showed hive_id -- the same
    // primary key the form used to ask for, and a number the user has
    // never seen anywhere else in the app.
    { key: "hive_id", header: "Hive", render: (r) => hiveLabel(r) },
    { key: "quantity", header: "Quantity", render: (r) => `${r.quantity} ${r.unit}` },
    { key: "varietal_name", header: "Varietal" },
    // Whether this row's honey is in the pool. Without it, the only way
    // to tell a credited harvest from an uncredited one is to add the
    // ledger up by hand.
    {
      key: "pool_movement_id",
      header: "In pool",
      render: (r) => (r.harvest_type !== "Honey" ? "—" : r.pool_movement_id ? "Yes" : "No"),
    },
    { key: "grade", header: "Grade" },
    { key: "batch_number", header: "Batch" },
    {
      key: "remove",
      header: "",
      render: (r) => <Button size="sm" variant="secondary" onClick={() => remove(r)}>Remove</Button>,
    },
  ];

  return (
    <div>
      <PageHeader title="Harvests">
        {hasFeature("HarvestProducts") && <Button onClick={() => setAddOpen(true)}>Log a harvest</Button>}
      </PageHeader>
      <LockedFeature enabled={hasFeature("HarvestProducts")} featureLabel="Harvest & Products" previewText="Track honey, wax, propolis, and pollen harvests across all your hives.">
        <Card>
          <Tabs
            onChange={changeTab}
            tabs={TYPES.map((type) => ({
              key: type,
              label: type,
              content: (
                <>
                  {/* No .filter() here any more. The server returns this
                      type only, so the pager below counts THIS tab --
                      filtering a page would have shown 10 of 50 mixed
                      rows under a total describing something else. */}
                  <DataTable
                    rows={harvests}
                    loading={harvests === null || loading}
                    rowKey="harvest_id"
                    emptyTitle={`No ${type.toLowerCase()} harvests logged`}
                    emptyDescription="Log a harvest to start building a record of what the season produced."
                    columns={columns}
                  />
                  <Pager
                    total={total}
                    limit={limit}
                    offset={offset}
                    onChange={goTo}
                    busy={loading}
                    unit={type.toLowerCase()}
                  />
                </>
              ),
            }))}
          />
        </Card>
      </LockedFeature>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Log a harvest"
        footer={<Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Log harvest"}</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/* Site first, then hive. The hive field was a NUMBER BOX asking
              for hive_id -- a primary key the user has never seen, and
              which is not the number on the hive: hive_number restarts at
              1 per site (#173), so somebody confidently typing "3" for
              "Hive 3" hit a different hive or a foreign-key error, with
              nothing in the form to catch it. HivePicker was built for
              exactly this in #211 and this form was missed.

              The site is a FILTER, not a saved field -- see V091 on why
              the harvest does not store one. It narrows the hive list,
              which is the whole job on an account with several yards. */}
          <SitePicker
            label="Site"
            value={form.site_id}
            onChange={(v) => setForm((f) => ({ ...f, site_id: v, hive_id: "" }))}
          />
          <HivePicker
            label="Hive"
            required
            value={form.hive_id}
            hives={sitedHives}
            // With a site chosen the list is one yard, so repeating its
            // name on every row buries the hive number that is the only
            // thing telling the rows apart.
            siteInLabel={!form.site_id}
            onChange={(v) => setForm((f) => ({ ...f, hive_id: v ?? "" }))}
          />

          {/* DIRECTLY UNDER THE HIVE FIELD, because it is about the hive
              just chosen and changes when that choice does. */}
          <WithdrawalNotice withdrawal={withdrawal} hiveId={form.hive_id} actionType="Harvest" />

          <Field label="What came off">
            {(id) => (
              <select id={id} className="touch-target" value={form.harvest_type}
                onChange={(e) => setForm((f) => ({ ...f, harvest_type: e.target.value }))}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </Field>
          {/* Honey only. Wax and propolis have no varietal, and offering
              one would put a meaningless label on the row. */}
          {isHoney && (
            <VarietalPicker
              /* RECORD: a harvest is honey that did not exist an hour ago,
                 so a put-away varietal is not offered (#358). */
              purpose="record"
              enabled={hasFeature("HoneyBusiness")}
              value={form.varietal_id}
              onChange={(v) => setForm((f) => ({ ...f, varietal_id: v }))}
              /* A harvest is a capture: the server resolves an omitted
                 varietal to General, so showing it says what will happen
                 instead of leaving the field blank and doing it anyway. */
              defaultGeneral
            />
          )}
          <Field label="How much">
            {(id) => <input id={id} type="number" step="0.1" className="touch-target" value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} />}
          </Field>
          <Field label="Unit">
            {(id) => (
              <select id={id} className="touch-target" value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            )}
          </Field>
          <Field label="Date">
            {(id) => <input id={id} type="date" className="touch-target" value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />}
          </Field>
          {/* The wire, made visible (V091). A honey harvest credits the
              pool by default -- that connection is the point -- but it is
              a checkbox rather than a certainty, because a beekeeper who
              logs supers off ten hives and THEN weighs the tank would
              otherwise bank the same honey twice, and a pool that
              double-counts is worse than one that misses.

              The pounds are spelled out because the harvest may be in
              gallons and the pool is always in pounds. Someone entering 5
              gal has no reason to know that 59.25 lb is about to appear
              in a balance they reconcile against a real tank. */}
          {isHoney && (
            <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start" }}>
              <input id="harvests-page-credit-pool" name="credit-pool"
                type="checkbox"
                checked={form.credit_pool}
                onChange={(e) => setForm((f) => ({ ...f, credit_pool: e.target.checked }))}
              />
              <span>
                Add this to the honey pool
                {poolPounds != null && form.unit !== "Lbs" ? (
                  <span style={{ color: "var(--color-text-muted)" }}>
                    {" "}— {form.quantity} {form.unit} is about {poolPounds} lb
                  </span>
                ) : null}
                <br />
                <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                  Leave this off if you'll weigh the tank and add it on the Honey Pool page instead.
                </span>
              </span>
            </label>
          )}
          <Field label="Note (optional)">
            {(id) => <input id={id} className="touch-target" value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />}
          </Field>
        </div>
      </Modal>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Remove this harvest?"
        confirmLabel="Remove"
        danger
        busy={busy}
      >
        {pendingDelete ? (
          <p>
            Removing the <strong>{pendingDelete.quantity} {pendingDelete.unit}{" "}
            {String(pendingDelete.harvest_type).toLowerCase()}</strong> harvest from{" "}
            <strong>{hiveLabel(pendingDelete)}</strong>. There is no undo.
            {pendingDelete.pool_movement_id ? (
              <> Its honey will be taken back out of the pool.</>
            ) : null}
          </p>
        ) : null}
      </ConfirmDialog>

    </div>
  );
}
